import { useEffect, useRef } from "react";
import { fetchPincodeDetailsAction } from "../actions/pincodeAction";

/**
 * usePincodeAutofill
 *
 * Watches a pincode value and auto-fills city + state when it reaches 6 digits.
 *
 * Behaviour:
 *  - Skips the very first call after `skipOnMount` is true (prevents overwriting
 *    city/state that were already prefilled from an API response).
 *  - Calls `onSuccess({ city, state })` when the pincode resolves.
 *  - Calls `onError(message)` when the pincode is invalid / API fails.
 *  - Calls `onClear()` when the pincode is cleared / incomplete (< 6 digits).
 *
 * @param {object} options
 * @param {string}   options.pincode       - Current pincode value (raw, may contain non-digits)
 * @param {boolean}  options.skipOnMount   - Skip the first valid 6-digit pincode hit
 *                                           (use when the form is pre-filled from an API)
 * @param {function} options.onSuccess     - ({ city, state }) => void
 * @param {function} [options.onError]     - (message: string) => void
 * @param {function} [options.onClear]     - () => void  — called when pin < 6 digits
 */
const usePincodeAutofill = ({
  pincode,
  skipOnMount = false,
  onSuccess,
  onError,
  onClear,
}) => {
  // Tracks the last pincode we already fetched — avoids duplicate API calls
  const lastFetchedPin = useRef(null);
  // Tracks whether we've seen the first valid pin (used for skipOnMount)
  const isFirstValidPin = useRef(true);

  useEffect(() => {
    const pin = String(pincode || "").replace(/\D/g, "");

    // console.log("[usePincodeAutofill] pincode changed →", {
    //   raw: pincode,
    //   cleaned: pin,
    //   length: pin.length,
    //   skipOnMount,
    //   isFirstValidPin: isFirstValidPin.current,
    //   lastFetchedPin: lastFetchedPin.current,
    // });

    // Incomplete — notify caller to clear city/state if needed
    if (pin.length < 6) {
      lastFetchedPin.current = null;
      onClear?.();
      return;
    }

    // Already fetched this exact pin — nothing to do
    if (pin === lastFetchedPin.current) {
      // console.log(
      //   "[usePincodeAutofill] skipping — already fetched this pin:",
      //   pin,
      // );
      return;
    }

    // Skip the very first hit when the form is pre-filled from an API.
    // NOTE: do NOT set lastFetchedPin here so that if the user manually
    // types the same pin as the prefilled one, it still triggers a fetch.
    if (skipOnMount && isFirstValidPin.current) {
      // console.log(
      //   "[usePincodeAutofill] skipping first hit (skipOnMount=true, prefilled pin):",
      //   pin,
      // );
      isFirstValidPin.current = false;
      // intentionally NOT setting lastFetchedPin so user can re-enter same pin
      return;
    }

    isFirstValidPin.current = false;
    lastFetchedPin.current = pin;

    // console.log("[usePincodeAutofill] fetching details for pin:", pin);
    fetchPincodeDetailsAction(pin).then((result) => {
      // console.log("[usePincodeAutofill] API result for pin", pin, "→", result);
      if (result.success) {
        onSuccess({ city: result.city, state: result.state });
      } else {
        onError?.(
          result.error ||
            "Invalid pin code, please enter the correct pin code.",
        );
      }
    });
  }, [pincode]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default usePincodeAutofill;
